"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import type { ReactNode } from "react";
import { FiInstagram, FiMail, FiMapPin } from "react-icons/fi";
import { routes } from "@/utils/routes";

// Reusable link item with enhanced accessibility and animations
function FooterLink({
  href,
  children,
  icon,
  external,
}: {
  href: string;
  children: ReactNode;
  icon?: ReactNode;
  external?: boolean;
}) {
  const rel = external ? "noopener noreferrer" : undefined;
  const target = external ? "_blank" : undefined;
  const hasIcon = Boolean(icon);

  return (
    <motion.li
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <a
        className={`group ${
          hasIcon
            ? "grid grid-cols-[32px_minmax(0,1fr)] items-center gap-4"
            : "inline-flex items-center"
        } min-h-[48px] rounded-md px-2 py-3 font-normal text-muted-foreground transition-all duration-300 ease-out hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
        href={href}
        rel={rel}
        target={target}
      >
        {/* Enhanced icon with hover animation */}
        {hasIcon ? (
          <motion.span
            className="flex h-8 w-8 items-center justify-center text-muted-foreground transition-colors duration-300 group-hover:text-primary"
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            whileHover={{ scale: 1.1 }}
          >
            {icon}
          </motion.span>
        ) : null}
        <span className="text-base">{children}</span>
      </a>
    </motion.li>
  );
}

export default function Footer() {
  return (
    <footer className="relative border-border border-t bg-background py-24">
      {/* Subtle top border with gradient */}
      <div className="absolute top-0 right-0 left-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      <motion.div
        className="container mx-auto px-4"
        initial={{ opacity: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, margin: "-100px" }}
        whileInView={{ opacity: 1 }}
      >
        {/* Primary footer content */}
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-3 lg:gap-24">
          {/* Brand Section */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            {/* Centered Brand Lockup */}
            <div className="mb-8 flex flex-col items-center space-y-6 lg:items-start">
              <Image
                alt="WillPower Store Logo"
                className="h-20 w-auto lg:h-24"
                height={96}
                loading="lazy"
                sizes="(min-width: 1024px) 96px, 80px"
                src="/images/logo.svg"
                width={96}
              />
              <h2 className="font-bold font-heading text-2xl text-foreground lg:text-3xl">
                WillPower Store
              </h2>
            </div>
            {/* Description */}
            <p className="body-text-base mx-auto max-w-sm text-center text-muted-foreground lg:mx-0 lg:text-left">
              Established in 2014, experienced in supporting fitness explorers.
              Certified well known brands, quality products and reliable
              services.
            </p>
          </motion.div>

          {/* Help Section */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="mb-8">
              <div className="relative mb-6">
                <h4 className="font-bold font-heading text-foreground text-xl">
                  Help
                </h4>
                <div className="-bottom-2 absolute left-0 h-1 w-12 rounded-full bg-primary" />
              </div>
            </div>
            <motion.div
              className="space-y-8"
              initial="hidden"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 },
                },
              }}
              viewport={{ once: true }}
              whileInView="visible"
            >
              <div>
                <h4 className="mb-4 font-bold text-foreground text-sm uppercase tracking-wide">
                  Support
                </h4>
                <motion.ul
                  className="space-y-2"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: { staggerChildren: 0.1 },
                    },
                  }}
                >
                  <FooterLink href={routes.about}>About Us</FooterLink>
                  <FooterLink href={routes.faq}>FAQ</FooterLink>
                </motion.ul>
              </div>
              <div>
                <h4 className="mb-4 font-bold text-foreground text-sm uppercase tracking-wide">
                  Legal
                </h4>
                <motion.ul
                  className="space-y-2"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: { staggerChildren: 0.1 },
                    },
                  }}
                >
                  <FooterLink href={routes.privacy}>Privacy Policy</FooterLink>
                  <FooterLink href={routes.terms}>Terms of Service</FooterLink>
                  <FooterLink href={routes.cookies}>Cookie Policy</FooterLink>
                </motion.ul>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="mb-8">
              <div className="relative mb-6">
                <h4 className="font-bold font-heading text-foreground text-xl">
                  Contact
                </h4>
                <div className="-bottom-2 absolute left-0 h-1 w-12 rounded-full bg-primary" />
              </div>
            </div>
            <motion.ul
              className="space-y-2"
              initial="hidden"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 },
                },
              }}
              viewport={{ once: true }}
              whileInView="visible"
            >
              <FooterLink
                external
                href="https://www.instagram.com/willpowerwellbeing/?hl=en"
                icon={<FiInstagram aria-hidden="true" className="h-5 w-5" />}
              >
                Instagram
              </FooterLink>
              <FooterLink
                href="mailto:willpowerwellbeing@hotmail.com"
                icon={<FiMail aria-hidden="true" className="h-5 w-5" />}
              >
                willpowerwellbeing@hotmail.com
              </FooterLink>
              <FooterLink
                external
                href="https://www.google.com/maps/search/?api=1&query=13+Great+Western+Arcade+Birmingham+UK+B32QD"
                icon={<FiMapPin aria-hidden="true" className="h-5 w-5" />}
              >
                13 Great Western Arcade
                <br />
                Birmingham UK B32QD
              </FooterLink>
            </motion.ul>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <motion.div
          className="relative mt-24 pt-8"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="flex flex-col items-center gap-2 text-center lg:items-start lg:text-left">
            <p className="font-normal text-muted-foreground text-sm">
              &copy; 2025 WillPower Store. All rights reserved.
            </p>
            <p className="font-normal text-muted-foreground/70 text-xs">
              Designed and developed by Gabriel Da Silva
            </p>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
}
